import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiMessageSquare } from "react-icons/fi";
import { useSupportMacros } from "./useSupportVocabulary";

/**
 * Canned-response picker for the ticket header.
 *
 * Picking a macro seeds the composer's draft rather than sending it — an agent
 * almost always edits the greeting or the specifics before it goes out.
 *
 * @param {Object} props
 * @param {string|null} [props.department] - Narrows to that pool's macros
 *   (department-less macros are always included by the backend).
 * @param {(body: string) => void} props.onPick - Receives the macro body.
 * @param {boolean} [props.disabled]
 */
export function MacroPicker({ department = null, onPick, disabled = false }) {
  const { macros, loading } = useSupportMacros(department);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onEscape = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onOutside);
    document.addEventListener("keydown", onEscape);
    return () => {
      document.removeEventListener("mousedown", onOutside);
      document.removeEventListener("keydown", onEscape);
    };
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return macros;
    return macros.filter((m) =>
      [m.title, m.shortcut, m.department]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(q))
    );
  }, [macros, query]);

  if (!loading && macros.length === 0) return null;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Insert a canned response"
        className="rounded-lg border border-gray-200 p-1.5 text-gray-600 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
      >
        <FiMessageSquare size={16} />
      </button>

      {open && (
        <div className="absolute right-0 z-20 mt-1 w-72 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg">
          <input
            type="text"
            value={query}
            autoFocus
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search macros or /shortcut"
            aria-label="Search macros"
            className="w-full border-b border-gray-100 px-3 py-2 text-xs text-gray-700 focus:outline-none"
          />
          <ul role="listbox" className="max-h-64 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-xs text-gray-400">
                {loading ? "Loading macros…" : "No macros match."}
              </li>
            ) : (
              filtered.map((macro, i) => (
                <li key={macro.id || macro.shortcut || i}>
                  <button
                    type="button"
                    onClick={() => {
                      setOpen(false);
                      setQuery("");
                      onPick(macro.body || "");
                    }}
                    className="block w-full px-3 py-2 text-left transition-colors hover:bg-orange-50"
                  >
                    <span className="block text-xs font-semibold text-gray-800">
                      {macro.title}
                      {macro.shortcut && (
                        <span className="ml-1 font-normal text-gray-400">
                          {macro.shortcut}
                        </span>
                      )}
                    </span>
                    <span className="block truncate text-[11px] text-gray-500">
                      {macro.body}
                    </span>
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

export default MacroPicker;
